import { Portal } from "client/Components";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback,
	useSynchronizedRef,
	useUnmountedCancellation,
} from "client/Hooks";
import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	FolderNavigationStackItem,
	createRootFolder
} from "../../WebDav/useFolderNavigationStack";
import {
	EnabledServer,
	ItemSelector,
	Folder
} from "./ItemSelector";
import { WebDavConfiguration, userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { IntegrationCredentialsLinkKind } from "client/Api/IntegrationCredentials/integrationCredentials";
import { showIncorrectWebDavCredentialsDialog } from "client_legacy/Documents/WebDav/Dialogs/incorrectCredentialsDialog";
import { SideBarMenu } from "./SideBarMenu";
import { CommonItemPickerOptions, WebDavPickerLocation } from "../ItemPicker";
import { isNil, isNotNil } from "client/Misc/utils";
import { GridItem } from "../../WebDav/WebDavOverview";
import { DocumentStoreType } from "client/Api/Documents/common";
import {
	LinkKind,
	useLinkToIntegrationCredentialsDialog
} from "client/Features/Settings/IntegrationCredentials/LinkToIntegrationCredentialsDialog";
import { offerPasswordUpdateWhenPossibleChanged } from "client/Features/Settings/IntegrationCredentials/SyncPassword";
import {
	getFolderNavigationStack,
	FileSystemItemModel,
	WebDavServerModel,
	listServers
} from "client/Api/Documents/WebDav/webDav";

export type ServerLoadingState =
	| { kind: "loading"; }
	| { kind: "error"; }
	| {
			kind: "success";
			servers: readonly WebDavServerModel[];
	  };

export type InitialWebDavLocation = {
	serverId: number | null;
	folderId: string;
};

export type WebDavPickerStoreProps = {
	onFilesSelect(files: readonly GridItem[]): void;
	selectedItems: readonly GridItem[];
	onLocationChange(folder: WebDavPickerLocation): void;
	sideBarContainer: HTMLElement;
	enabledServer?: EnabledServer;
	commonOptions: CommonItemPickerOptions;
	isActive: boolean;
	isMobile: boolean;
	location: WebDavPickerLocation | undefined;
	onSidebarClose: () => void;
	shouldOpenFileOnClick: boolean;
	webDavConfiguration: WebDavConfiguration;
};

export const WebDavPickerStore = (props: WebDavPickerStoreProps): ReactElement => {
	const {
		sideBarContainer,
		enabledServer,
		onFilesSelect,
		selectedItems,
		commonOptions,
		isActive,
		location,
		onLocationChange,
		isMobile,
		onSidebarClose,
		shouldOpenFileOnClick,
		webDavConfiguration
	} = props;

	const [serverLoadingState, setServerLoadingState] = useState<ServerLoadingState>({ kind: "loading" });
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [linkToIntegrationCredentialsDialog, openLinkToIntegrationCredentialsDialog] = useLinkToIntegrationCredentialsDialog();

	const currentServer: WebDavServerModel | undefined = location?.folder.server;

	const handleDataRetrievalError = useCallback(
		async (): Promise<void> => {
			const { integrationCredentialsConfiguredLinks } = userConfiguration();
			if (!integrationCredentialsConfiguredLinks.fileServers) {
				await openLinkToIntegrationCredentialsDialog({
					thingToLink: {
						kind: LinkKind.FileServers
					}
				});
			} else {
				if (serverLoadingState.kind === "error"){
					showIncorrectWebDavCredentialsDialog();
					return;
				}
				if (!await offerPasswordUpdateWhenPossibleChanged({ kind: IntegrationCredentialsLinkKind.FileServers })) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}
		},
		[
			alertManager,
			openLinkToIntegrationCredentialsDialog,
			serverLoadingState.kind
		]
	);

	const stack: readonly FolderNavigationStackItem[] =
		useFolderStack(location?.folder, handleDataRetrievalError);

	const isActiveRef: ReadonlyRefObject<boolean> = useSynchronizedRef(isActive);
	const initialLocationRef: ReadonlyRefObject<WebDavPickerLocation | undefined> = useSynchronizedRef(location);
	const stableOnLocationChange: typeof onLocationChange = useStableImperativeCallback(onLocationChange);

	const loadServers = useCallback(
		async (signal: AbortSignal): Promise<void> => {
			try {
				setServerLoadingState({ kind: "loading" });
				const servers: readonly WebDavServerModel[] = await listServers(signal);

				setServerLoadingState(
					servers.length > 0
						? {
							kind: "success",
							servers
						}
						: { kind: "error" }
				);

				if (!isActiveRef.current) {
					return;
				}

				const initialServer: WebDavServerModel | undefined = initialLocationRef.current
					?.folder
					.server;

				const selectedServer = isNotNil(initialServer)
					? initialServer
					: servers[0];

				if(selectedServer === undefined) {
					throw new Error("expected server");
				}

				stableOnLocationChange({
					kind: DocumentStoreType.WebDav,
					folder: initialLocationRef.current
						?.folder
							? initialLocationRef.current
								?.folder
							: {
								...createRootFolder(selectedServer),
								server: selectedServer
							}
				});
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setServerLoadingState({ kind: "error" });
			}
		},
		[
			alertManager,
			initialLocationRef,
			isActiveRef,
			stableOnLocationChange
		]
	);

	useEffect(
		(): ()=> void => {
			const cancellation = new AbortController();
			loadServers(cancellation.signal);

			return () => cancellation.abort();
		},
		[loadServers]
	);

	const openFolderHandler = useCallback(
		(folder: FolderNavigationStackItem): void => {
			if (isNil(currentServer)) {
				return;
			}
			onLocationChange({
				kind: DocumentStoreType.WebDav,
				folder: {...folder, server: currentServer}
			});
		},
		[currentServer, onLocationChange]
	);

	const selectServerHandler = useCallback(
		(server: WebDavServerModel): void => {
			onLocationChange({
				kind: DocumentStoreType.WebDav,
				folder: {...createRootFolder(server), server}
			});

			if (isMobile) {
				onSidebarClose();
			}
		},
		[
			isMobile,
			onLocationChange,
			onSidebarClose
		]
	);


	return (
		<>
			<Portal container={sideBarContainer}>
				<SideBarMenu
					currentServerId={isActive ? currentServer?.id : undefined}
					serverLoadingState={serverLoadingState}
					onSelectServer={selectServerHandler}
					onReloadSideBar={() => loadServers(globalCancellation)}
					enabledServer={enabledServer}
				/>
			</Portal>
			{!isActive || !location?.folder
				? null
				: (
					<ItemSelector
						stack={stack}
						currentFolder={location.folder}
						onFilesSelect={onFilesSelect}
						selectedItems={selectedItems}
						commonOptions={commonOptions}
						isMobile={isMobile}
						onOpenFolder={openFolderHandler}
						shouldOpenFileOnClick={shouldOpenFileOnClick}
						webDavConfiguration={webDavConfiguration}
					/>
				)
			}
			{linkToIntegrationCredentialsDialog}
		</>
	);
};

WebDavPickerStore.displayName = "WebDavPickerStore";

function useFolderStack(
	selectedFolder: Folder | undefined,
	onDataRetrievalError: () => Promise<void>

	): readonly FolderNavigationStackItem[]{
	const [stack, setStack] = useState<readonly FolderNavigationStackItem[]>([]);
	const savedStacksRef = useRef<Map<string, readonly FolderNavigationStackItem[]>>(new Map());

	const loadBreadCrumbs = useCallback(
		async (folder: Folder | undefined): Promise<void> => {
			try {
				if (isNil(folder)) {
					return;
				}

				const rootFolder: FolderNavigationStackItem = createRootFolder(folder.server);
				const resultStack: FolderNavigationStackItem[] = [rootFolder];

				if(folder.id === rootFolder.id) {
					 setStack(resultStack);
					 return;
				}

				const currentFolderUniqueId: string = `${folder.server.id}${folder.id}`;

				const currentSavedStack: readonly FolderNavigationStackItem[] | undefined =
					savedStacksRef.current
						.get(currentFolderUniqueId);

				if (isNotNil(currentSavedStack)) {
					setStack(currentSavedStack);
					return;
				}

				const parentFolderUniqueId: string = `${folder.server.id}${folder.parentId}`;
				const parentSavedStack: readonly FolderNavigationStackItem[] | undefined =
					savedStacksRef.current
						.get(parentFolderUniqueId);

				if (isNotNil(parentSavedStack)) {
					const newStack: readonly FolderNavigationStackItem[] = [
						...parentSavedStack,
						{
							id: folder.id,
							parentId: folder.parentId,
							name: folder.name
						}
					];

					savedStacksRef.current
						.set(currentFolderUniqueId, newStack);

					setStack(newStack);
					return;
				}

				const folders: FileSystemItemModel[] = await getFolderNavigationStack(
					folder.server.id,
					folder.id
				);

				folders.forEach((x: FileSystemItemModel) => {
					resultStack.push({
						id: x.id,
						parentId: x.parentId,
						name: x. name
					});
				});

				savedStacksRef.current
					.set(currentFolderUniqueId, resultStack);

				setStack(resultStack);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				onDataRetrievalError();
				console.error("Failed to load the folder stack from the backend", e);
				setStack([]);
			}
		},
		[onDataRetrievalError]
	);

	useEffect(
		(): void => {
			loadBreadCrumbs(selectedFolder);
		},
		[loadBreadCrumbs, selectedFolder]
	);

	return stack;
}